import { useEffect, useState } from 'react';
import { adminApi } from '../lib/api';
import { Star, Clock, Plus, Edit2, Trash2, Eye, EyeOff, X, Check } from 'lucide-react';
import { CATEGORIES, CITIES } from '../data/categories';

const EMPTY = {
  name: '',
  rating: 4.5,
  reviews: 0,
  categories: [],
  treatments: [],
  location: '',
  city: 'Oslo',
  badge: '',
  wait_time: 'Innen 1 uke',
  placeholder: false,
  featured: false,
  active: true,
  sort_order: 0,
};

export default function AdminKlinikker() {
  const [data, setData] = useState([]);
  const [sort, setSort] = useState('leads');
  const [editing, setEditing] = useState(null); // null | 'new' | clinicObj
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = () =>
    adminApi.get('/admin/clinics-stats').then((r) => setData(r.data.clinics || []));

  useEffect(() => { load(); }, []);

  const sorted = [...data].sort((a, b) => {
    if (sort === 'rating') return (b.rating || 0) - (a.rating || 0);
    if (sort === 'leads') return (b.leads_received || 0) - (a.leads_received || 0);
    if (sort === 'featured') return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
    if (sort === 'inactive') return (a.active ? 1 : 0) - (b.active ? 1 : 0);
    return 0;
  });

  const startNew = () => { setEditing('new'); setForm({ ...EMPTY }); setErr(''); };
  const startEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || '',
      rating: c.rating ?? 4.5,
      reviews: c.reviews ?? 0,
      categories: c.categories || [],
      treatments: c.treatments || [],
      location: c.location || '',
      city: c.city || 'Oslo',
      badge: c.badge || '',
      wait_time: c.waitTime || c.wait_time || 'Innen 1 uke',
      placeholder: !!c.placeholder,
      featured: !!c.featured,
      active: c.active !== false,
      sort_order: c.sort_order || 0,
    });
    setErr('');
  };

  const close = () => { setEditing(null); setForm(EMPTY); setErr(''); };

  const allTreatments = CATEGORIES.flatMap((c) => c.treatments).filter((t, i, a) => a.indexOf(t) === i);

  const save = async () => {
    setErr('');
    if (!form.name.trim()) { setErr('Navn er påkrevd.'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await adminApi.post('/admin/clinics', form);
      } else {
        await adminApi.patch(`/admin/clinics/${editing.id}`, form);
      }
      await load();
      close();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Kunne ikke lagre klinikk.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    await adminApi.patch(`/admin/clinics/${c.id}`, { active: !(c.active !== false) });
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Slette ${c.name} permanent? Dette kan ikke angres.`)) return;
    await adminApi.delete(`/admin/clinics/${c.id}`);
    load();
  };

  const toggleArr = (key, value) => {
    setForm((f) => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#0A1F44]">Klinikker</h1>
          <p className="text-[#64748B] text-sm mt-1">Administrer partnerklinikker — legg til, skjul eller fjern.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white border border-[#0A1F44]/10 rounded-lg px-4 py-2 text-sm" data-testid="sort-clinics">
            <option value="leads">Flest leads</option>
            <option value="rating">Høyest rating</option>
            <option value="featured">Featured først</option>
            <option value="inactive">Skjulte først</option>
          </select>
          <button onClick={startNew} data-testid="add-clinic-btn" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
            <Plus size={14} /> Ny klinikk
          </button>
        </div>
      </header>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sorted.map((c) => {
          const isHidden = c.active === false;
          return (
            <div
              key={c.id}
              className={`bg-white rounded-xl p-5 border ${isHidden ? 'border-amber-300 bg-amber-50/40' : 'border-[#0A1F44]/10'}`}
              data-testid={`admin-clinic-${c.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-1">
                  <Star size={13} fill="#3B82F6" className="text-[#3B82F6]" />
                  <span className="font-semibold text-[#0A1F44]">{c.rating}</span>
                  <span className="text-xs text-[#64748B]">({c.reviews})</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {isHidden && <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Skjult</span>}
                  {c.featured && <span className="text-[10px] uppercase font-bold text-[#3B82F6]">Partner</span>}
                  {c.placeholder && <span className="text-[10px] uppercase font-bold text-[#64748B]">Placeholder</span>}
                </div>
              </div>
              <div className="font-display text-lg font-bold text-[#0A1F44] mb-0.5">{c.name}</div>
              <div className="text-xs text-[#64748B] mb-3">{c.location || '—'}</div>
              <div className="flex items-center gap-1 text-xs text-[#64748B] mb-3"><Clock size={11} /> {c.waitTime || c.wait_time || '—'}</div>
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#0A1F44]/5 text-xs mb-3">
                <div><div className="text-[#64748B]">Leads</div><div className="font-bold text-[#0A1F44]">{c.leads_received}</div></div>
                <div><div className="text-[#64748B]">Vist</div><div className="font-bold text-[#0A1F44]">{c.times_shown}</div></div>
                <div><div className="text-[#64748B]">Konv.</div><div className="font-bold text-[#3B82F6]">{c.conversion}%</div></div>
              </div>
              <div className="flex items-center gap-2 pt-3 border-t border-[#0A1F44]/5">
                <button onClick={() => startEdit(c)} data-testid={`edit-clinic-${c.id}`} title="Rediger" className="flex-1 px-3 py-1.5 bg-[#FAF8F5] hover:bg-[#0A1F44]/10 rounded-lg text-xs font-semibold text-[#0A1F44] flex items-center justify-center gap-1.5"><Edit2 size={12} /> Rediger</button>
                <button onClick={() => toggleActive(c)} data-testid={`toggle-active-${c.id}`} title={isHidden ? 'Vis igjen' : 'Skjul'} className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 ${isHidden ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700' : 'bg-amber-50 hover:bg-amber-100 text-amber-700'}`}>
                  {isHidden ? <><Eye size={12} /> Vis</> : <><EyeOff size={12} /> Skjul</>}
                </button>
                <button onClick={() => remove(c)} data-testid={`delete-clinic-${c.id}`} title="Slett" className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-semibold flex items-center gap-1.5"><Trash2 size={12} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={close}>
          <div className="bg-white rounded-2xl p-7 max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="clinic-edit-modal">
            <div className="flex items-start justify-between mb-5">
              <h3 className="font-display text-2xl font-bold text-[#0A1F44]">
                {editing === 'new' ? 'Ny klinikk' : `Rediger ${editing.name}`}
              </h3>
              <button onClick={close}><X /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Field label="Navn *">
                <input data-testid="cf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Adresse / lokasjon">
                <input data-testid="cf-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} placeholder="Oslo sentrum" />
              </Field>
              <Field label="By">
                <select data-testid="cf-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls}>
                  {CITIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </Field>
              <Field label="Ventetid">
                <input data-testid="cf-wait" value={form.wait_time} onChange={(e) => setForm({ ...form, wait_time: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Rating (0–5)">
                <input data-testid="cf-rating" type="number" min={0} max={5} step={0.1} value={form.rating} onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value) || 0 })} className={inputCls} />
              </Field>
              <Field label="Antall anmeldelser">
                <input data-testid="cf-reviews" type="number" min={0} value={form.reviews} onChange={(e) => setForm({ ...form, reviews: parseInt(e.target.value) || 0 })} className={inputCls} />
              </Field>
              <Field label="Badge (f.eks. 'Mest valgt')">
                <input data-testid="cf-badge" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Sortering (lavt = først)">
                <input data-testid="cf-sort" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className={inputCls} />
              </Field>
            </div>

            <Field label="Kategorier" className="mt-5">
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => {
                  const on = form.categories.includes(c.name);
                  return (
                    <button key={c.slug} type="button" onClick={() => toggleArr('categories', c.name)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${on ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'bg-white text-[#0A1F44] border-[#0A1F44]/20 hover:border-[#0A1F44]/40'}`}>
                      {on && <Check size={11} className="inline mr-1" />}{c.name}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="Behandlinger som tilbys" className="mt-4">
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 bg-[#FAF8F5] rounded-lg">
                {allTreatments.map((t) => {
                  const on = form.treatments.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleArr('treatments', t)} className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${on ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'bg-white text-[#0A1F44] border-[#0A1F44]/15 hover:border-[#0A1F44]/30'}`}>
                      {on && <Check size={10} className="inline mr-0.5" />}{t}
                    </button>
                  );
                })}
              </div>
            </Field>

            <div className="flex flex-wrap gap-5 mt-5 text-sm">
              <Toggle label="Aktiv (synlig på siden)" testid="cf-active" checked={form.active} onChange={(v) => setForm({ ...form, active: v })} />
              <Toggle label="Featured (Partner)" testid="cf-featured" checked={form.featured} onChange={(v) => setForm({ ...form, featured: v })} />
              <Toggle label="Placeholder (kommer snart)" testid="cf-placeholder" checked={form.placeholder} onChange={(v) => setForm({ ...form, placeholder: v })} />
            </div>

            {err && <div className="mt-4 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</div>}

            <div className="mt-6 flex gap-2">
              <button onClick={save} disabled={saving} data-testid="save-clinic" className="bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-60 text-white px-5 py-2.5 rounded-lg text-sm font-semibold">
                {saving ? 'Lagrer…' : (editing === 'new' ? 'Opprett klinikk' : 'Lagre endringer')}
              </button>
              <button onClick={close} className="bg-[#FAF8F5] text-[#0A1F44] px-5 py-2.5 rounded-lg text-sm">Avbryt</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-[#FAF8F5] border-0 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30";

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-[11px] uppercase tracking-wider font-semibold text-[#64748B] mb-1.5">{label}</label>
    {children}
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#3B82F6]" />
    <span className="text-[#0A1F44]">{label}</span>
  </label>
);
